import { joinLines, splitLines } from "@/app/form";
import type { RoutingRule, RoutingRuleInput } from "@/app/routing";
import type { Errors } from "@/app/useDraft";
import { itemDraft, type ItemDraft } from "./routeConditionDraft";
import { routeConditionError } from "./routeConditions";
import { parseTarget, targetKey, targetName, type TargetNames } from "./targetDraft";

/** 规则草稿：条件、目标与打开时的并发版本；路由没有时间条件（D132）。 */
export interface RoutingRuleDraft {
  name: string; version: number; item: ItemDraft;
}

/** 捕获编辑时的版本，MNC保持原样，避免覆盖其他运营的保存。 */
export function routingRuleDraft(row?: RoutingRule): RoutingRuleDraft {
  const m = row?.match;
  const item = itemDraft();
  if (m) Object.assign(item, { mcc: m.mcc, mnc: m.mnc, senders_on: !!m.senders?.length, senders: joinLines(m.senders) });
  item.target = targetKey(row?.channel_group_id ?? null, row?.channel_id ?? null);
  return { name: row?.name ?? "", version: row?.version ?? 0, item };
}

/** 换算独立规则；规则不携带绑定优先级。 */
export function routingRuleInput(d: RoutingRuleDraft): { body?: RoutingRuleInput; errors: Errors<RoutingRuleDraft> } {
  const errors: Errors<RoutingRuleDraft> = {};
  const it = d.item;
  if (!d.name.trim()) errors.name = "填写规则名称。";
  const match = { mcc: it.mcc, mnc: it.mnc, senders: it.senders_on ? splitLines(it.senders) : [] };
  const conditionError = routeConditionError(match);
  if (conditionError) errors.item = conditionError;
  if (it.senders_on && !match.senders.length) errors.item = "填写 sender，或移除 Sender 条件。";
  if (!/^[cg][1-9]\d*$/.test(it.target)) errors.item = "选择通道或通道组合。";
  if (Object.keys(errors).length) return { errors };
  const target = parseTarget(it.target);
  return { errors, body: { name: d.name.trim(), version: d.version, match,
    ...(target.channel ? { channel_id: target.channel } : { channel_group_id: target.group! }) } };
}

/** 规则目标的统一显示名称，列表与模板明细共用；规则只指向通道或通道组合（D131）。 */
export function routingRuleTargetName(r: Pick<RoutingRule, "channel_group_id" | "channel_id">, names: TargetNames): string {
  if (r.channel_group_id) return `通道组合 ${names.group(r.channel_group_id)}`;
  return targetName(null, r.channel_id ?? null, names);
}

/** 一条规则的匹配摘要："MCC/MNC（sender A,B）→ 目标名"；MCC 留空为"不限"，MNC 留空为"*"，通道组合名后加 *，名称取不到时为 #id（组合为 #id*）。 */
export function matchLine(r: Pick<RoutingRule, "match" | "channel_id" | "channel_group_id">, names: TargetNames): string {
  const m = r.match, where = m.mcc ? `${m.mcc}/${m.mnc || "*"}` : "不限";
  // 目标只写名字：通道组合在名字后加 *（含义见“目标详情”表头悬停提示，读屏另读“（通道组合）”，不只靠星号），取不到名字时为 #id。
  const target = r.channel_group_id ? `${names.group(r.channel_group_id)}*` : r.channel_id ? names.channel(r.channel_id) : routingRuleTargetName(r, names);
  return `${where}${m.senders?.length ? `（sender ${m.senders.join(",")}）` : ""} → ${target}`;
}
